import i18nFactory from '@yandex-int/i18n';
import { isEmpty } from 'lodash';
import * as React from 'react';

import { Bem } from '../../service/Bem/Bem';
import {
    getRetargetingConditionsText,
    getRetargetingConditionTypeText
} from '../../service/RetargetingHelpers/RetargetingHelpers';
import { getUserProfileConditionTitle } from '../../service/UserProfileHelpers/UserProfileHelpers';
import { BasePopupProps } from '../../typings/common';
import {
    CopyTooltipStatus,
    RetargetingConditionRule
} from '../../typings/domain';
import { UserProfileCondition } from '../../typings/userProfileRetargeting';
import { PopupDecorator } from '../PopupDecorator/PopupDecorator';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';
import { StyledLink, StyledLinkTheme } from '../StyledLink/StyledLink';
import { ScopedTooltip } from '../TooltipDecorator/ScopedTooltip';

import './UsersProfileConditionInfoTooltip.css';
import * as keyset from './UsersProfileConditionInfoTooltip.i18n';

const i18n = i18nFactory(keyset);

export interface UsersProfileConditionInfoTooltipProps {
    userProfileConditions: Array<UserProfileCondition>;
    metrikaAudienceGoals: RetargetingConditionRule[];
    onCopyClick?: () => void;
    copyTooltipStatus: CopyTooltipStatus;
}

const BLOCK = 'users-profile-condition-info-tooltip';

export class UsersProfileConditionInfoTooltip extends React.PureComponent<
    UsersProfileConditionInfoTooltipProps & BasePopupProps
> {
    private anchor?: React.ReactNode;
    private copiedTooltipAnchor?: React.ReactNode;

    render() {
        const { openPopup } = this.props;

        return (
            <>
                <StyledLink
                    theme={StyledLinkTheme.grey}
                    cls={Bem.cls(BLOCK, 'link')}
                    onClick={openPopup}
                    ref={ref => (this.anchor = ref)}
                >
                    {i18n('open-popup-text')}
                    <Shape2
                        cls={Bem.cls(BLOCK, 'icon-info')}
                        icon={Shape2Icon.info}
                        theme={Shape2Theme.default}
                        width={12}
                        height={12}
                    />
                </StyledLink>
                {this.getPopup()}
            </>
        );
    }

    private getPopup = () => {
        const {
            onCopyClick,
            isPopupVisible,
            closePopup,
            copyTooltipStatus
        } = this.props;

        return (
            <PopupDecorator
                cls={Bem.cls(BLOCK, 'popup')}
                onClose={closePopup}
                target={'anchor'}
                anchor={this.anchor}
                directions={['right-top', 'right-center', 'right-bottom']}
                visible={isPopupVisible}
                hasTail={true}
                hasClose={true}
                onOutsideClick={closePopup}
                header={i18n('popup-title')}
            >
                {this.renderPopUpDescription()}
                <StyledLink
                    theme={StyledLinkTheme.grey}
                    cls={Bem.cls(BLOCK, 'link-copy')}
                    onClick={onCopyClick}
                    ref={ref => (this.copiedTooltipAnchor = ref)}
                >
                    <Shape2
                        icon={Shape2Icon.copy}
                        theme={Shape2Theme.default}
                        width={16}
                        height={16}
                        cls={Bem.cls(BLOCK, 'icon-copy')}
                    />
                    {i18n('copy-link')}
                </StyledLink>
                <ScopedTooltip
                    anchor={this.copiedTooltipAnchor}
                    theme={
                        copyTooltipStatus === CopyTooltipStatus.error
                            ? 'error'
                            : 'normal'
                    }
                    view={'classic'}
                    tone={'default'}
                    to={'right'}
                    size={'s'}
                    visible={copyTooltipStatus !== CopyTooltipStatus.hidden}
                >
                    {copyTooltipStatus === CopyTooltipStatus.error
                        ? i18n('copy-error')
                        : i18n('copied')}
                </ScopedTooltip>
            </PopupDecorator>
        );
    };

    private renderPopUpDescription = () => {
        return (
            <div className={Bem.cls(BLOCK, 'popup-description')}>
                {this.renderUserProfile()}
                {this.renderMetrika()}
            </div>
        );
    };

    private renderUserProfile = () => {
        const { userProfileConditions } = this.props;

        return isEmpty(userProfileConditions) ? null : (
            <>
                <div className={Bem.cls(BLOCK, 'popup-title')}>
                    {i18n('users-profiles')}
                </div>
                {this.renderUserProfileDescription()}
            </>
        );
    };

    private renderMetrika = () => {
        const { metrikaAudienceGoals } = this.props;

        return isEmpty(metrikaAudienceGoals) ? null : (
            <>
                <div className={Bem.cls(BLOCK, 'popup-title')}>
                    {i18n('metrikaAudience')}
                </div>
                {this.renderRetargetingGoalsDescription()}
            </>
        );
    };

    private renderUserProfileDescription = () => {
        const { userProfileConditions } = this.props;

        return userProfileConditions.map(condition => {
            const { key, type, values, interestType } = condition;

            return (
                <div key={key} className={Bem.cls(BLOCK, 'popup-condition')}>
                    <div className={Bem.cls(BLOCK, 'title-condition')}>
                        {`${getUserProfileConditionTitle(
                            key,
                            type,
                            interestType
                        )}:`}
                    </div>
                    <div className={Bem.cls(BLOCK, 'text-condition')}>
                        {values.join(', ')}
                    </div>
                </div>
            );
        });
    };

    private renderRetargetingGoalsDescription = () => {
        const { metrikaAudienceGoals } = this.props;

        return metrikaAudienceGoals.map((conditionItem, index) => {
            const { type, goals } = conditionItem;

            return (
                <div key={index} className={Bem.cls(BLOCK, 'popup-condition')}>
                    <div className={Bem.cls(BLOCK, 'title-condition')}>
                        {`${getRetargetingConditionTypeText(type)}:`}
                    </div>
                    <div className={Bem.cls(BLOCK, 'text-condition')}>
                        {getRetargetingConditionsText(goals)}.
                    </div>
                </div>
            );
        });
    };
}
